/**
 * WorkTable + Pager — the frozen table contract, rendered once for the whole
 * product. The pager consumes a PageView from ANY PageSource adapter and
 * renders the complete frozen footer — range "of N", windowed numbers,
 * direct jump to any page, Previous/Next — from the server's totals
 * (ZOY-285) or an array's real length. No number is invented.
 */

import { useIntl } from 'react-intl';
import s from './table.module.css';
import { Button, Skeleton } from './kit';
import { PER_PAGE_OPTIONS, pageWindow, type PageView } from '@/core/api/pageSource';

export type ColumnTier = 'all' | 'tablet-up' | 'desktop-only';

export interface Column {
  id: string;
  label: string;
  tier?: ColumnTier;
  /** CSS grid track, e.g. '1.6fr' or 'auto'. */
  width?: string;
  align?: 'start' | 'end';
  /** Custom header content (sort/filter affordances); `label` stays the accessible name. */
  header?: React.ReactNode;
}

function tierClass(tier: ColumnTier | undefined): string {
  if (tier === 'desktop-only') return s.desktopOnly!;
  if (tier === 'tablet-up') return s.tabletUp!;
  return '';
}

export function WorkTable<T>(props: {
  columns: readonly Column[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  renderCell: (row: T, columnId: string) => React.ReactNode;
  loading?: boolean;
  /**
   * ZOY-411: the list query failed. The table renders an explicit failed
   * state and REFUSES to render `rows` — with keepPreviousData they may be
   * the previous query's page, and a failed request must never present
   * stale rows (or a cheerful empty state) as its result. The toolbar stays
   * usable; the footer (pager totals) is suppressed with the rows.
   */
  error?: boolean;
  onRetry?: () => void;
  empty?: { title: string; body: string; action?: React.ReactNode };
  toolbar?: React.ReactNode;
  footer?: React.ReactNode;
  'aria-label'?: string;
}) {
  const intl = useIntl();
  // ONE grid for the whole table. The header and every row are subgrids of
  // it, so an `auto` track is sized once, by the widest cell in the whole
  // column — header included — and never per row (which is what let a
  // short "OWED" header sit over a wide "1,000,000 Toman" cell). Three
  // templates, one per responsive tier, because a column dropped at a
  // breakpoint must drop its track too; the stylesheet picks the variable.
  // fr tracks are wrapped in minmax(0, …): a bare `1.7fr` keeps an implicit
  // min-content floor, so a wide primary cell refuses to shrink and shoves
  // the actions column off-screen on narrow widths; minmax(0, …) lets it
  // shrink (the cell ellipsises) so the actions always stay reachable.
  const track = (c: Column) => {
    const w = (c.width ?? '1fr').trim();
    return /fr$/.test(w) ? `minmax(0, ${w})` : w;
  };
  const template = (keep: (c: Column) => boolean) =>
    props.columns.filter(keep).map(track).join(' ') || 'minmax(0, 1fr)';
  const vars = {
    '--wt-cols-all': template(() => true),
    '--wt-cols-tablet': template((c) => c.tier !== 'desktop-only'),
    '--wt-cols-mobile': template((c) => c.tier === undefined || c.tier === 'all'),
  } as React.CSSProperties;
  return (
    <div className={s.wrap}>
      {props.toolbar && <div className={s.toolbar}>{props.toolbar}</div>}
      <div role="table" aria-label={props['aria-label']} className={s.table} style={vars}>
        <div className={s.head} role="row">
          {props.columns.map((c) => (
            <div
              key={c.id}
              role="columnheader"
              className={[s.headCell, c.align === 'end' ? s.cellEnd : '', tierClass(c.tier)].join(
                ' ',
              )}
              aria-label={c.header !== undefined ? c.label : undefined}
            >
              {c.header ?? c.label}
            </div>
          ))}
        </div>

        {props.loading &&
          [0, 1, 2, 3].map((i) => (
            <div key={i} className={s.row}>
              <div className={s.span}>
                <Skeleton width="60%" />
              </div>
            </div>
          ))}

        {!props.loading && props.error && (
          <div className={s.stateBox} role="alert">
            <div className={s.stateTitle}>{intl.formatMessage({ id: 'table.failedTitle' })}</div>
            <div className={s.stateBody}>{intl.formatMessage({ id: 'table.failedBody' })}</div>
            {props.onRetry && (
              <Button variant="quiet" onClick={props.onRetry}>
                {intl.formatMessage({ id: 'table.retry' })}
              </Button>
            )}
          </div>
        )}

        {!props.loading && !props.error && props.rows.length === 0 && props.empty && (
          <div className={s.stateBox}>
            <div className={s.stateTitle}>{props.empty.title}</div>
            <div className={s.stateBody}>{props.empty.body}</div>
            {props.empty.action}
          </div>
        )}

        {!props.loading &&
          !props.error &&
          props.rows.map((row) => (
            <div key={props.rowKey(row)} className={s.row} role="row">
              {props.columns.map((c) => (
                <div
                  key={c.id}
                  role="cell"
                  className={[s.cell, c.align === 'end' ? s.cellEnd : '', tierClass(c.tier)].join(
                    ' ',
                  )}
                >
                  {props.renderCell(row, c.id)}
                </div>
              ))}
            </div>
          ))}
      </div>
      {!props.error && props.footer}
    </div>
  );
}

export function PrimaryCell(props: {
  avatar?: string;
  main: React.ReactNode;
  sub?: React.ReactNode;
}) {
  return (
    <span className={s.primaryCell}>
      {props.avatar !== undefined && (
        <span className={s.avatar} aria-hidden>
          {props.avatar}
        </span>
      )}
      <span className={s.primaryText}>
        <span className={s.primaryMain}>{props.main}</span>
        {props.sub !== undefined && <span className={s.primarySub}>{props.sub}</span>}
      </span>
    </span>
  );
}

/** Content shown ONLY on narrow screens — the stacked facts a dropped column would have carried. */
export function MobileOnly({ children }: { children: React.ReactNode }) {
  return <span className={s.mobileOnly}>{children}</span>;
}

/** A header affordance (sort / filter) — quiet, 44px hit area, states in ARIA. */
export function HeadButton(
  props: React.ButtonHTMLAttributes<HTMLButtonElement> & { on?: boolean },
) {
  const { on, className, ...rest } = props;
  return (
    <button
      type="button"
      {...rest}
      className={[s.headBtn, on ? s.headBtnOn : '', className ?? ''].join(' ')}
    />
  );
}

export function RowButton(
  props: React.ButtonHTMLAttributes<HTMLButtonElement> & { danger?: boolean },
) {
  const { danger, className, ...rest } = props;
  return (
    <button
      type="button"
      {...rest}
      className={[s.rowBtn, danger ? s.rowBtnDanger : '', className ?? ''].join(' ')}
    />
  );
}

export function TableSearch(props: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <span className={s.search}>
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden
      >
        <path d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z" />
        <path d="M21 21l-4.3-4.3" />
      </svg>
      <input
        className={s.searchInput}
        value={props.value}
        onChange={(e) => props.onChange(e.currentTarget.value)}
        placeholder={props.placeholder}
        aria-label={props.placeholder}
      />
    </span>
  );
}

export function Pager(props: {
  view: PageView<unknown>;
  perPage: number;
  onPerPage: (n: number) => void;
  onPage: (p: number) => void;
}) {
  const intl = useIntl();
  const { view } = props;
  const num = (n: number) => intl.formatNumber(n);
  const range =
    view.range === null
      ? ''
      : view.total !== null
        ? intl.formatMessage(
            { id: 'table.rangeOf' },
            { from: num(view.range.from), to: num(view.range.to), total: num(view.total) },
          )
        : intl.formatMessage(
            { id: 'table.range' },
            { from: num(view.range.from), to: num(view.range.to) },
          );

  return (
    <div className={s.pager}>
      <span className={s.range}>{range}</span>
      <span
        className={s.perPage}
        role="group"
        aria-label={intl.formatMessage({ id: 'table.perPage' })}
      >
        {PER_PAGE_OPTIONS.map((n) => (
          <button
            key={n}
            type="button"
            className={[s.perOpt, n === props.perPage ? s.perOn : ''].join(' ')}
            aria-pressed={n === props.perPage}
            onClick={() => props.onPerPage(n)}
          >
            {num(n)}
          </button>
        ))}
      </span>
      <span className={s.pagerSpacer} />
      <span className={s.pageBtns}>
        <button
          type="button"
          className={s.pageBtn}
          disabled={!view.hasPrev}
          onClick={() => props.onPage(view.page - 1)}
        >
          {intl.formatMessage({ id: 'table.previous' })}
        </button>
        {pageWindow(view).map((p, i) =>
          p === 'gap' ? (
            <span key={`g${i}`} className={s.gapDots} aria-hidden>
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={[s.pageBtn, p === view.page ? s.pageOn : ''].join(' ')}
              aria-current={p === view.page ? 'page' : undefined}
              aria-label={intl.formatMessage({ id: 'table.page' }, { n: num(p) })}
              onClick={() => props.onPage(p)}
            >
              {num(p)}
            </button>
          ),
        )}
        <button
          type="button"
          className={s.pageBtn}
          disabled={!view.hasNext}
          onClick={() => props.onPage(view.page + 1)}
        >
          {intl.formatMessage({ id: 'table.next' })}
        </button>
      </span>
      <span className={s.keptNote}>{intl.formatMessage({ id: 'table.keptNote' })}</span>
    </div>
  );
}
